import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance, { authHeaders } from "../../utils/axios";

// Async thunks
export const fetchRoles = createAsyncThunk(
  "adminRoles/fetchAll",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("roles", {
        ...authHeaders(),
        params
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const createRole = createAsyncThunk(
  "adminRoles/create",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("roles", data, authHeaders());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateRole = createAsyncThunk(
  "adminRoles/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`roles/${id}`, data, authHeaders());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteRole = createAsyncThunk(
  "adminRoles/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(`roles/${id}`, authHeaders());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const toggleRoleStatus = createAsyncThunk(
  "adminRoles/toggleStatus",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`roles/${id}/status`, {}, authHeaders());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchPermissions = createAsyncThunk(
  "adminRoles/fetchPermissions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("permissions", authHeaders());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateRolePermissions = createAsyncThunk(
  "adminRoles/updatePermissions",
  async ({ id, permissionIds }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`roles/${id}/permissions`, { permissionIds }, authHeaders());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

const adminRoleSlice = createSlice({
  name: "adminRoles",
  initialState: {
    roles: [],
    permissions: [],
    isLoading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Roles
      .addCase(fetchRoles.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.roles = action.payload.data;
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create Role
      .addCase(createRole.fulfilled, (state, action) => {
        state.roles.unshift(action.payload.data);
      })
      // Update Role
      .addCase(updateRole.fulfilled, (state, action) => {
        const index = state.roles.findIndex((r) => r.id === action.meta.arg.id);
        if (index !== -1) {
          // Since our backend returns "Role updated successfully", we might need to refetch or update manually
          // For simplicity, we'll assume the local state update is handled by a refetch in the component
        }
      })
      // Delete Role
      .addCase(deleteRole.fulfilled, (state, action) => {
        const index = state.roles.findIndex((r) => r.id === action.meta.arg);
        if (index !== -1) {
          state.roles[index].is_active = false;
        }
      })
      // Toggle Role Status
      .addCase(toggleRoleStatus.fulfilled, (state, action) => {
        const index = state.roles.findIndex((r) => r.id === action.meta.arg);
        if (index !== -1) {
          state.roles[index].is_active = action.payload.data.is_active;
        }
      })
      // Fetch Permissions
      .addCase(fetchPermissions.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.permissions = action.payload.data;
      })
      .addCase(fetchPermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update Role Permissions
      .addCase(updateRolePermissions.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateRolePermissions.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateRolePermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError } = adminRoleSlice.actions;
export default adminRoleSlice.reducer;
